import { useEffect, useState } from "react";
import { Phone, MessageSquare, TrendingUp, Clock } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import api from "@/lib/api";

const EMPTY_VOL = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => ({ d, calls: 0, chats: 0 }));

function EmptyOverlay({ show, label }) {
  if (!show) return null;
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <span className="text-xs text-muted-foreground bg-card/70 px-3 py-1 rounded-full">{label}</span>
    </div>
  );
}

export default function Analytics() {
  const [kpis, setKpis] = useState({});
  const [data, setData] = useState({ volume: EMPTY_VOL, channels: [], sentiment: [], has_volume: false, has_channels: false, has_sentiment: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/dashboard/kpis").then((r) => r.data).catch(() => ({})),
      api.get("/dashboard/agent-analytics").then((r) => r.data).catch(() => null),
    ]).then(([k, a]) => {
      if (cancelled) return;
      setKpis(k || {});
      if (a) setData({ ...a, volume: (a.volume && a.volume.length) ? a.volume : EMPTY_VOL });
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="analytics-page">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight">Analytics</h2>
        <p className="text-sm text-muted-foreground">Your performance and conversation insights — last 7 days.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Phone} label="Calls handled" value={loading ? "—" : (kpis.calls_handled ?? 0)} testid="kpi-calls-handled" />
        <Kpi icon={Clock} label="Avg handle time" value={loading ? "—" : (kpis.aht || "—")} testid="kpi-aht" />
        <Kpi icon={MessageSquare} label="Open conversations" value={loading ? "—" : (kpis.open_conversations ?? 0)} testid="kpi-open-conversations" />
        <Kpi icon={TrendingUp} label="Login hours" value={loading ? "—" : (kpis.login_hours || "—")} testid="kpi-login-hours" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5">
          <h3 className="font-semibold mb-3">Conversation volume — last 7 days</h3>
          <div className="h-64 relative" style={{ minHeight: 240 }} data-testid="analytics-volume-chart">
            <EmptyOverlay show={!loading && !data.has_volume} label="No activity in the last 7 days yet" />
            <ResponsiveContainer>
              <AreaChart data={data.volume} margin={{ top: 5, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="c1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="c2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="d" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="calls" name="Calls" stroke="hsl(var(--primary))" fill="url(#c1)" strokeWidth={2} />
                <Area type="monotone" dataKey="chats" name="Chats" stroke="hsl(var(--accent))" fill="url(#c2)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-semibold mb-3">Channel mix</h3>
          <div className="h-64 relative" style={{ minHeight: 240 }} data-testid="analytics-channel-chart">
            <EmptyOverlay show={!loading && !data.has_channels} label="No conversations yet" />
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.channels} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {data.channels.map((c, i) => <Cell key={i} fill={c.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-3 rounded-xl border border-border bg-card p-5">
          <h3 className="font-semibold mb-3">Sentiment (positive vs. negative)</h3>
          <div className="h-64 relative" style={{ minHeight: 240 }} data-testid="analytics-sentiment-chart">
            <EmptyOverlay show={!loading && !data.has_sentiment} label="Sentiment appears once the AI Copilot analyses conversations" />
            <ResponsiveContainer>
              <BarChart data={data.sentiment} margin={{ top: 5, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="d" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="pos" name="Positive" fill="hsl(var(--chart-4))" radius={[6, 6, 0, 0]} />
                <Bar dataKey="neg" name="Negative" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-overline text-muted-foreground">{label}</div>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="font-display font-extrabold text-2xl mt-2">{value}</div>
    </div>
  );
}
